import {
  AGENCY_CURRENT_OPTIONS,
  aiFlaggedFilter,
  aiFlaggedOrderBy,
  attentionLaneAFilter,
  attentionLaneAOrderBy,
  attentionLaneBFilter,
  callsOnDayFilter,
  callsOnDayOrderBy,
  type DayBounds,
  ENROLLMENT_ACTIVE_OPTION,
  HEALTH_ATTENTION_OPTIONS,
  HEALTH_RED_OPTION,
  healthRedFilter,
  healthRedOrderBy,
  moneyWindow,
  outstandingPaymentsFilter,
  outstandingPaymentsOrderBy,
  PAYMENT_SETTLED_OPTIONS,
  unscoredEnrollmentsFilter,
} from '@ofmos/shared/dashboard';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { OrderBy } from '@ofmos/shared/record-query';
import type { FieldMetadata, ObjectMetadata } from '../../data/client';
import type { RecordListOptions } from '../../data/hooks';

/**
 * WHAT EACH PANEL ASKS FOR, as pure functions over runtime metadata.
 *
 * ONE QUERY PER PANEL, AND THE TILE IS THAT QUERY (dashboard.md D15). Not "the same
 * idea" - the same object, the same filter, the same bounds, the same cache entry. Both
 * the tile and the panel call `useRecordList` with the options `rowsOptions` returns, so
 * TanStack serves them from ONE request and the number above cannot drift from the list
 * below. The board already learned this the expensive way: "a Pipeline board whose stage
 * counts are wrong is worse than no counts at all."
 *
 * THE ROW BUDGET IS WHY EVERY `select` IS SPELLED OUT HERE. A panel's rows carry only
 * what ONE `RecordQuery` against ONE object plus the platform's existing label batching
 * can return: a relation resolves to `{id, label}` and nothing more, batched one
 * statement per relation FIELD. So ONE HOP IS FREE AND TWO HOPS DO NOT EXIST - from an
 * enrollment you can have the agency's LABEL, never that agency's CSM; from a payment you
 * can have the deal's label, never the deal's agency. Both of those were in the first
 * draft of the document and both are dropped rather than bought with a second query.
 *
 * RESOLUTION IS BY NAME, WHICH IS A KNOWN DEVIATION FROM INV-7 (D16, section 9). Universal
 * id would be the better key and is NOT AVAILABLE: `POST /api/metadata/objects` mints one
 * itself and no caller can set it, so a universal-id-resolved panel could not be
 * exercised by any test this repository can write. Nothing here is PERSISTED - resolution
 * happens per render against live metadata - so renaming an object removes its panel
 * until it is renamed back, which is the ABSENT state and not a repair job.
 */

export interface PanelQuery {
  readonly objectId: string;
  readonly select: readonly string[];
  readonly filter: FilterNode;
  readonly orderBy: readonly OrderBy[];
  /** Field ids by name, so a row can read the values it selected. */
  readonly fields: Readonly<Record<string, string>>;
}

/** Five rows per lane, matching the related sections' answer to the same question. */
export const LANE_MAX_ROWS = 5;
/**
 * The cap on a single-list panel before its "See all" (D31's `--panel-max-rows`).
 *
 * A NUMBER IN TYPESCRIPT AND NOT A CSS VARIABLE, which is the one place this module
 * departs from D31's table: it is a QUERY LIMIT, and a custom property no stylesheet
 * reads is dead code that cannot be consumed by the thing that needs it. The other eight
 * measures in that table are real tokens in `ui/tokens.css`.
 */
export const PANEL_MAX_ROWS = 8;

export function objectByName(
  objects: readonly ObjectMetadata[] | undefined,
  apiName: string,
): ObjectMetadata | undefined {
  return objects?.find((object) => object.name === apiName);
}

/**
 * A field lookup that returns `undefined` for anything missing, so a panel whose object
 * exists WITHOUT the fields it needs degrades exactly like an absent one rather than
 * building a query with an empty field id in it.
 */
export function fieldsByName(
  fields: readonly FieldMetadata[] | undefined,
): ReadonlyMap<string, FieldMetadata> {
  return new Map((fields ?? []).map((field) => [field.name, field]));
}

/**
 * An option id from its LABEL, because the browser has no other key.
 *
 * The id is a hash of the field's universal id and the option KEY, minted server-side;
 * the key is not on the wire. `shared/dashboard.ts` carries both vocabularies so the two
 * resolvers cannot drift, and a renamed label drops its condition - which lands the panel
 * in ABSENT, the same blast radius as api-name resolution.
 */
export function optionIdByLabel(
  field: FieldMetadata | undefined,
  label: string,
): string | undefined {
  return field?.options?.selectOptions?.find((option) => option.label === label)?.id;
}

/** All present, or nothing. A partial resolution is an absent panel, never a half query. */
function allResolved<T>(values: readonly (T | undefined)[]): values is readonly T[] {
  return values.every((value) => value !== undefined);
}

function idsOf(fields: ReadonlyMap<string, FieldMetadata>, names: readonly string[]) {
  return names.map((name) => fields.get(name)?.id);
}

/** Lane A row: the agency's label, the score, its age, the reason, the run. */
export const HEALTH_RED_FIELDS = [
  'name',
  'health',
  'health_set_at',
  'primary_risk_reason',
  'phase',
  'agency',
] as const;

export function healthRedQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const select = idsOf(fields, HEALTH_RED_FIELDS);
  const statusField = fields.get('enrollment_status');
  const healthField = fields.get('health');
  const red = optionIdByLabel(healthField, HEALTH_RED_OPTION.label);
  const active = optionIdByLabel(statusField, ENROLLMENT_ACTIVE_OPTION.label);
  if (!allResolved(select) || statusField === undefined || red === undefined) return null;
  if (healthField === undefined || active === undefined) return null;

  return {
    objectId: object.id,
    select: [...select, statusField.id],
    filter: healthRedFilter({
      health: healthField.id,
      healthRedOptionId: red,
      enrollmentStatus: statusField.id,
      enrollmentActiveOptionId: active,
    }),
    orderBy: healthRedOrderBy({ healthSetAt: fields.get('health_set_at')?.id ?? '' }),
    fields: namesToIds(fields, [...HEALTH_RED_FIELDS, 'enrollment_status']),
  };
}

/**
 * THE PHASE B LANES (`phase-b-coach.md` D15), beside the two above rather than instead of
 * them: `healthRedQuery` is what the panel read before the engine existed, and it is left
 * where it is so a reader can see what changed.
 *
 * WHAT A LANE A ROW CARRIES, and why it is exactly this: the colour is the capsule, the
 * FIRST reason line is the whole sentence a coach needs to decide whether this is today's
 * problem, and `health_since` is the age - the day the colour STARTED, not the day the
 * engine last looked, which is what makes "since 4 days" a fact about the customer rather
 * than about the job.
 *
 * `primary_risk_reason` IS GONE FROM THIS QUERY. It was the hand-typed reason, D14 moves its
 * values into the coach's note and hides the field, and reading it here would put a sentence
 * from March under a colour computed this morning.
 */
export const ATTENTION_LANE_A_FIELDS = [
  'name',
  'health',
  'health_since',
  'health_reason',
  'phase',
  'agency',
] as const;

export function attentionLaneAQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const select = idsOf(fields, ATTENTION_LANE_A_FIELDS);
  const statusField = fields.get('enrollment_status');
  const healthField = fields.get('health');
  const sinceField = fields.get('health_since');
  const attention = HEALTH_ATTENTION_OPTIONS.map((option) =>
    optionIdByLabel(healthField, option.label),
  );
  const active = optionIdByLabel(statusField, ENROLLMENT_ACTIVE_OPTION.label);
  if (!allResolved(select) || statusField === undefined || healthField === undefined) return null;
  if (sinceField === undefined || active === undefined || !allResolved(attention)) return null;

  return {
    objectId: object.id,
    select: [...select, statusField.id],
    filter: attentionLaneAFilter({
      health: healthField.id,
      healthAttentionOptionIds: attention,
      enrollmentStatus: statusField.id,
      enrollmentActiveOptionId: active,
    }),
    orderBy: attentionLaneAOrderBy({ healthSince: sinceField.id }),
    fields: namesToIds(fields, [...ATTENTION_LANE_A_FIELDS, 'enrollment_status']),
  };
}

/**
 * THE INBOX'S HEALTH KIND, AND IT IS THE SAME PREDICATE WITH THE SORT TURNED AROUND
 * (P3 round 2, F1; ledger P3-11 named this query and P3-13 builds it).
 *
 * Lane A is ordered `health_since ASC` because the panel's question is "what has been
 * wrong LONGEST"; the inbox's question is the opposite one, "what turned red or yellow
 * TODAY", and on any installation with five or more standing red or yellow enrollments
 * the panel's cached page can never contain the answer. So this is a SECOND cache entry
 * over the same filter - `orderBy` is part of the list signature and therefore part of
 * the key - and it is the only new request Home makes.
 *
 * `nulls: 'last'` and not lane A's `'first'`: an enrollment with no `health_since` has no
 * day to have turned on, so it belongs at the end of a newest-first sort rather than at
 * the head of it.
 */
export function inboxHealthTurnedQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const base = attentionLaneAQuery(object, fields);
  const sinceField = fields.get('health_since');
  if (base === null || sinceField === undefined) return null;
  return { ...base, orderBy: [{ fieldId: sinceField.id, direction: 'desc', nulls: 'last' }] };
}

/** Lane B: "the system says this is not green and no human has looked at it since". */
export const ATTENTION_LANE_B_FIELDS = [
  'name',
  'health',
  'health_since',
  'health_reason',
  'needs_assessment',
  'agency',
] as const;

export function attentionLaneBQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const select = idsOf(fields, ATTENTION_LANE_B_FIELDS);
  const statusField = fields.get('enrollment_status');
  const needsField = fields.get('needs_assessment');
  const sinceField = fields.get('health_since');
  const active = optionIdByLabel(statusField, ENROLLMENT_ACTIVE_OPTION.label);
  if (!allResolved(select) || statusField === undefined || needsField === undefined) return null;
  if (sinceField === undefined || active === undefined) return null;

  return {
    objectId: object.id,
    select: [...select, statusField.id],
    filter: attentionLaneBFilter({
      needsAssessment: needsField.id,
      enrollmentStatus: statusField.id,
      enrollmentActiveOptionId: active,
    }),
    /** The oldest unanswered colour first, the same order and the same reason as lane A. */
    orderBy: attentionLaneAOrderBy({ healthSince: sinceField.id }),
    fields: namesToIds(fields, [...ATTENTION_LANE_B_FIELDS, 'enrollment_status']),
  };
}

/**
 * D15'S HONEST EMPTY STATE: "Nothing Needs Attention. N enrollments have no signal yet."
 *
 * The count is the ONE number that separates "everybody is fine" from "the engine cannot see
 * anything", and without it an empty panel on a dead feeder reads as a good morning.
 *
 * IT COUNTS AN EMPTY `health`, which is the engine's own way of saying it declined to answer:
 * since the stale-guard decision the engine withholds the green floor when a guard has fired
 * and nothing was found, so an enrollment with no colour is exactly an enrollment nothing
 * could be said about - whether the touch data never arrived or the exporter stopped last
 * Tuesday. Counting `touch_refreshed_at isEmpty` on the AGENCY instead would have missed the
 * second case entirely, which is the one that actually happens.
 *
 * ONE OBJECT, ONE FILTER. It is the enrollment's own field, so both filter backends can
 * answer it and the sentence survives in the review build and in every browser test - which a
 * count that crossed a table to the agency could not (`relation-filters.md` D14 makes those
 * SQL-only by name).
 *
 * It is D22's own query, unchanged: "no health score" and "no signal" are the same row set,
 * and what moved is the SENTENCE - Phase B knows why the score is missing.
 */
export function unscoredEnrollmentsQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const statusField = fields.get('enrollment_status');
  const healthField = fields.get('health');
  const nameField = fields.get('name');
  const active = optionIdByLabel(statusField, ENROLLMENT_ACTIVE_OPTION.label);
  if (statusField === undefined || healthField === undefined || nameField === undefined)
    return null;
  if (active === undefined) return null;
  return {
    objectId: object.id,
    select: [nameField.id],
    filter: unscoredEnrollmentsFilter({
      enrollmentStatus: statusField.id,
      enrollmentActiveOptionId: active,
      health: healthField.id,
    }),
    orderBy: [],
    fields: namesToIds(fields, ['name', 'health', 'enrollment_status']),
  };
}

/** Lane B row: one object, no relations, nothing to price. */
export const AI_FLAGGED_FIELDS = [
  'name',
  'ai_risk_flag',
  'ai_risk_reason',
  'ai_risk_flagged_at',
  'lifecycle_status',
] as const;

export function aiFlaggedQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
): PanelQuery | null {
  const select = idsOf(fields, AI_FLAGGED_FIELDS);
  const flagField = fields.get('ai_risk_flag');
  const lifecycleField = fields.get('lifecycle_status');
  const current = AGENCY_CURRENT_OPTIONS.map((option) =>
    optionIdByLabel(lifecycleField, option.label),
  );
  if (!allResolved(select) || flagField === undefined || lifecycleField === undefined) return null;
  if (!allResolved(current)) return null;

  return {
    objectId: object.id,
    select,
    filter: aiFlaggedFilter({
      aiRiskFlag: flagField.id,
      lifecycleStatus: lifecycleField.id,
      currentLifecycleOptionIds: current,
    }),
    orderBy: aiFlaggedOrderBy({ aiRiskFlaggedAt: fields.get('ai_risk_flagged_at')?.id ?? '' }),
    fields: namesToIds(fields, AI_FLAGGED_FIELDS),
  };
}

/** The rail's row: a time, a name, a type chip, and three relation LABELS. */
export const CALL_FIELDS = [
  'name',
  'scheduled_at',
  'occurred_at',
  'call_type',
  'host',
  'agency',
  'lead',
] as const;

export function callsOnDayQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
  bounds: DayBounds,
): PanelQuery | null {
  const select = idsOf(fields, CALL_FIELDS);
  const scheduled = fields.get('scheduled_at');
  const occurred = fields.get('occurred_at');
  if (!allResolved(select) || scheduled === undefined || occurred === undefined) return null;

  return {
    objectId: object.id,
    select,
    filter: callsOnDayFilter({ scheduledAt: scheduled.id, occurredAt: occurred.id }, bounds),
    orderBy: callsOnDayOrderBy({ scheduledAt: scheduled.id }),
    fields: namesToIds(fields, CALL_FIELDS),
  };
}

/** The money row: the payment, the amount, the due badge, and the DEAL's label. */
export const MONEY_FIELDS = ['name', 'amount', 'due_date', 'payment_status', 'deal'] as const;

export function moneyQuery(
  object: ObjectMetadata,
  fields: ReadonlyMap<string, FieldMetadata>,
  todayKey: string,
): PanelQuery | null {
  const select = idsOf(fields, MONEY_FIELDS);
  const paidAt = fields.get('paid_at');
  const dueDate = fields.get('due_date');
  const statusField = fields.get('payment_status');
  const settled = PAYMENT_SETTLED_OPTIONS.map((option) =>
    optionIdByLabel(statusField, option.label),
  );
  if (!allResolved(select) || paidAt === undefined || dueDate === undefined) return null;
  if (statusField === undefined || !allResolved(settled)) return null;

  return {
    objectId: object.id,
    select: [...select, paidAt.id],
    filter: outstandingPaymentsFilter(
      {
        paidAt: paidAt.id,
        dueDate: dueDate.id,
        paymentStatus: statusField.id,
        settledOptionIds: settled,
      },
      moneyWindow(todayKey),
    ),
    orderBy: outstandingPaymentsOrderBy({ dueDate: dueDate.id }),
    fields: namesToIds(fields, [...MONEY_FIELDS, 'paid_at']),
  };
}

function namesToIds(
  fields: ReadonlyMap<string, FieldMetadata>,
  names: readonly string[],
): Readonly<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const name of names) {
    const id = fields.get(name)?.id;
    if (id !== undefined) map[name] = id;
  }
  return map;
}

/**
 * THE OPTIONS BOTH THE TILE AND THE PANEL PASS TO `useRecordList`.
 *
 * ONE FUNCTION, so the two callers cannot produce two signatures - and since the
 * signature IS the cache key (INV-13), one function means one cache entry and therefore
 * one request. `withTotalCount` is always on: the tile reads the server's total over the
 * whole filtered set, never the length of the page the panel happens to be showing.
 *
 * The document asks for the tile's query to be the panel's "apart from `limit` and
 * `withTotalCount`". Being literally the same query is the stronger form of that rule and
 * costs one request instead of two, which is also what the query budget asks for.
 */
export function rowsOptions(query: PanelQuery, limit: number): RecordListOptions {
  return {
    select: query.select,
    filter: query.filter,
    orderBy: query.orderBy,
    limit,
    withTotalCount: true,
  };
}

/** The empty-state counts: a total and no rows worth reading. */
export function countOnlyOptions(query: PanelQuery): RecordListOptions {
  return { select: query.select, filter: query.filter, limit: 1, withTotalCount: true };
}

/**
 * WHAT A PANEL NEEDS BEFORE IT CAN RESOLVE AT ALL.
 *
 * D16 defines ABSENT as "the panel's object is not in `useObjects()`", which is the case
 * it was written for. There is a second one the document does not name and the fixture
 * model demonstrates: an object with the right API NAME and none of the fields the panel
 * reads. A `call` object with no `scheduled_at` cannot produce a day rail, and rendering
 * "nothing recorded for this day" over it would be the ABSENT/EMPTY collapse D19 forbids
 * - "there is nothing to show you" told where the truth is "this installation does not
 * have that concept".
 *
 * So field presence decides ABSENT alongside object presence, and the Customize list says
 * WHICH field is missing. Same state, same zero pixels, one more honest sentence.
 */
export const PANEL_REQUIRED_FIELDS: Readonly<Record<string, readonly string[]>> = {
  attention: [...ATTENTION_LANE_A_FIELDS, 'enrollment_status'],
  today: [...CALL_FIELDS],
  openItems: [],
  money: [...MONEY_FIELDS, 'paid_at'],
};

/** The first field a panel needs and does not have, or `undefined` when it has them all. */
export function missingFieldFor(
  panelId: string,
  fields: ReadonlyMap<string, FieldMetadata>,
): string | undefined {
  return (PANEL_REQUIRED_FIELDS[panelId] ?? []).find((name) => !fields.has(name));
}
